import type { ShopifyProduct } from "@/types/shopify";
import type { StringProductMeta, ProductFamily } from "@/types/catalog";
import { presetForGauges } from "@/lib/configurator/preset-sets";
import {
  LINE_ALIAS,
  PRODUCT_TYPE_CATEGORY,
  detectGaugeLabel,
  resolveLineSlug,
} from "@/lib/shopify/sync-config";

// Lines that may display on the storefront. Anything else (Arkay, Hybrid,
// RockPack, unknown) is deprecated/non-catalog and is filtered out so it never
// renders, mirroring the active StringLine allowlist the sync enforces.
const ALLOWED_LINE_SLUGS = new Set([
  "aurora-one",
  "ultra-sheer",
  "ultra-flex",
  "prime",
  "stainless-steel",
]);

// ─── Line facts ───────────────────────────────────────────
//
// This used to be a LINE_SPECS table asserting a material, coating, tension
// profile, tone profile and recommended tuning for every product line —
// including "Nickel-Wound" for nylon classical sets — none of it sourced from
// the catalog. Shopify's material/tension metafields are empty across the
// export, so the honest position is that we do not have those specs.
//
// What survives here is only what the line name itself tells us.

/** Lines whose name states the material outright. */
const LINE_MATERIAL: Record<string, string> = {
  "stainless-steel": "Stainless steel",
};

/** Short, factual description of what distinguishes a line. No tone rankings. */
const LINE_BLURB: Record<string, string> = {
  "aurora-one": "Aurora's original coated line. The widest colour range and the only line offered across every instrument family.",
  "ultra-sheer": "A coated line offered on electric, acoustic, and bass sets.",
  "ultra-flex": "A coated line offered on electric, acoustic, and bass sets.",
  prime: "A coated line offered on electric, acoustic, and bass sets.",
  "stainless-steel": "Uncoated stainless steel. Offered on bass and on the lighter electric sets.",
};

// ─── Known product line names (longest match first to avoid partial hits) ────

const KNOWN_LINES: Array<{ name: string; slug: string }> = [
  { name: "Aurora Premium", slug: "aurora-premium" },
  { name: "Ultra-Sheer", slug: "ultra-sheer" },
  { name: "Ultra-Flex", slug: "ultra-flex" },
  { name: "Aurora One", slug: "aurora-one" },
  { name: "Stainless Steel", slug: "stainless-steel" },
  { name: "Prime", slug: "prime" },
];

// Canonical display name per line slug (used when an alias resolves to a
// canonical line, e.g. "aurora-premium" → "aurora-one").
const LINE_NAME_BY_SLUG: Record<string, string> = Object.fromEntries(
  KNOWN_LINES.map((l) => [l.slug, l.name])
);

// ─── Gauge sort order ─────────────────────────────────────

const GAUGE_ORDER: string[] = [
  "extra-light",
  "light",
  "medium-light",
  "custom-light",
  "medium",
  "light-heavy",
  "big-heavy",
  "heavy",
  "extra-heavy",
];

function gaugeRank(gaugeName: string): number {
  const key = gaugeName.toLowerCase().replace(/\s+/g, "-");
  const idx = GAUGE_ORDER.indexOf(key);
  return idx === -1 ? 999 : idx;
}

// ─── Line sort order ──────────────────────────────────────

const LINE_ORDER: string[] = [
  "aurora-premium",
  "ultra-sheer",
  "ultra-flex",
  "prime",
  "aurora-one",
  "stainless-steel",
];

function lineRank(slug: string): number {
  const idx = LINE_ORDER.indexOf(slug);
  return idx === -1 ? LINE_ORDER.length : idx;
}

// ─── Helpers ──────────────────────────────────────────────

/** The fields parseStringProduct reads; structural so partial fixtures work. */
export type CatalogSourceProduct = Pick<
  ShopifyProduct,
  "handle" | "title" | "productType" | "metafields"
>;

function getMetafieldValue(
  product: CatalogSourceProduct,
  key: string
): string | null {
  if (!product.metafields) return null;
  const field = product.metafields.find((f) => f?.key === key);
  return field?.value ?? null;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function lineFromTitle(title: string) {
  const titleUpper = title.toUpperCase().replace(/-/g, " ");
  for (const { name, slug } of KNOWN_LINES) {
    if (titleUpper.startsWith(name.toUpperCase().replace(/-/g, " "))) {
      return { name, slug };
    }
  }
  return null;
}

function categoryFromProductType(productType: string): string | null {
  return PRODUCT_TYPE_CATEGORY[productType.trim()] ?? null;
}

function gaugeListFromHandle(handle: string, gaugeLabel: string | null): string | null {
  if (!gaugeLabel) return null;

  const nums = [...handle.replace(/\d+-strings?\b/gi, "").matchAll(/(?:^|-)(\d*\.\d+|\d+)(?=-|$)/g)]
    .map((m) => Number(m[1]))
    .map((n) => n < 1 ? Number((n * 1000).toPrecision(15)) : n)
    .filter((n) => n >= 8 && n <= 150);

  if (nums.length < 2) return null;

  let values = nums;
  const range = gaugeLabel.match(/^(\d+)-(\d+)$/);
  if (range && nums.length > 2) {
    const low = parseInt(range[1], 10);
    const high = parseInt(range[2], 10);
    const rest = nums.slice(2);
    if (nums[0] === low && nums[1] === high && rest.length >= 2 && rest[0] === low && rest.at(-1) === high) {
      values = rest;
    }
  }

  return values.join(" | ");
}

// ─── formatGaugeRange ─────────────────────────────────────

/**
 * Derive a short "low-high" range from a pipe-separated gauge string.
 * "09 | 11 | 16 | 24 | 32 | 42" → "9-42". Leading zeros are stripped.
 * Returns null when fewer than two numeric values are present.
 */
export function formatGaugeRange(individualGauges: string): string | null {
  const nums = individualGauges
    .split("|")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !Number.isNaN(n));
  if (nums.length < 2) return null;
  return `${nums[0]}-${nums[nums.length - 1]}`;
}

// ─── parseStringProduct ───────────────────────────────────

export function parseStringProduct(
  product: Pick<ShopifyProduct, "handle" | "title" | "productType" | "metafields">
): StringProductMeta {
  const title = product.title;

  // 1. Determine productLine — metafield first, then handle, then title parse.
  let productLine = "";
  let productLineSlug = "";
  const handleLineSlug = resolveLineSlug(product.handle);
  const titleLine = lineFromTitle(title);

  const metaLine = getMetafieldValue(product, "product_line");
  if (metaLine) {
    productLine = metaLine;
    productLineSlug = slugify(metaLine);
  } else {
    if (handleLineSlug) {
      productLineSlug = handleLineSlug;
      productLine = LINE_NAME_BY_SLUG[handleLineSlug] ?? "";
    } else {
      if (titleLine) {
        productLine = titleLine.name;
        productLineSlug = titleLine.slug;
      }
    }
  }

  // 1b. Resolve aliased lines (e.g. legacy "aurora-premium" → "aurora-one")
  // so they group and display under the canonical line.
  if (productLineSlug && LINE_ALIAS[productLineSlug]) {
    productLineSlug = LINE_ALIAS[productLineSlug];
    productLine = LINE_NAME_BY_SLUG[productLineSlug] ?? productLine;
  }

  // 2. instrument — from productType, strip " Strings" suffix
  const instrument = product.productType.replace(/\s*Strings$/i, "").trim();

  // 3. gaugeName — metafield first, then extract from title
  let gaugeName = "";
  const metaGauge = getMetafieldValue(product, "gauge_label");
  if (metaGauge) {
    gaugeName = metaGauge;
  } else {
    // Pattern: everything between "Strings " and "," in the title
    const stringsCommaMatch = title.match(/Strings\s+([^,]+),/i);
    if (stringsCommaMatch) {
      gaugeName = stringsCommaMatch[1].trim();
    }
    if (!gaugeName) {
      const category = categoryFromProductType(product.productType);
      gaugeName = category ? detectGaugeLabel(product.handle, category) ?? "" : "";
    }
  }

  // 4. individualGauges — metafield first, then handle, then after-comma title.
  //
  // Some Shopify records disagree with themselves: the handle says one set and
  // the title says another (e.g. handle ".../medium-4-string-45-105-45-65-85-105"
  // titled "ULTRA FLEX MEDIUM (5 STRING) 45-125, 45 | 65 | 85 | 105 | 125").
  // When the two sources conflict we cannot tell which is correct, so we assert
  // NEITHER and flag it, rather than printing a gauge list and a string count
  // that contradict the product's own name.
  let individualGauges = "";
  const metaGauges = getMetafieldValue(product, "individual_gauges");
  const category = categoryFromProductType(product.productType) ?? "electric";
  const handleGauges = gaugeListFromHandle(product.handle, detectGaugeLabel(product.handle, category) ?? gaugeName);
  const commaIdx = title.indexOf(",");
  const titleSuffix = commaIdx !== -1 ? title.slice(commaIdx + 1).trim() : "";
  const titleGauges = /^(?:\d*\.\d+|\d+)(?:\s*\|\s*(?:\d*\.\d+|\d+))+$/.test(titleSuffix) ? titleSuffix : "";
  const normalize = (v: string) =>
    v
      .split("|")
      .map((g) => Number(g.trim()))
      .map((n) => n < 1 ? Number((n * 1000).toPrecision(15)) : n)
      .join(",");
  const handleCount = product.handle.match(/(?:^|-)(\d+)-strings?\b/i)?.[1];
  const titleCount = title.match(/\b(\d+)[\s-]+strings?\b/i)?.[1];
  const gaugeConflict = Boolean(
    (handleGauges && titleGauges && normalize(handleGauges) !== normalize(titleGauges)) ||
    (handleCount && titleCount && Number(handleCount) !== Number(titleCount)) ||
    (handleLineSlug && titleLine &&
      (LINE_ALIAS[handleLineSlug] ?? handleLineSlug) !== (LINE_ALIAS[titleLine.slug] ?? titleLine.slug))
  );
  if (!gaugeConflict) {
    individualGauges = metaGauges || handleGauges || titleGauges;
  }

  if (productLineSlug && !gaugeName) {
    console.warn(`[parseStringProduct] Could not extract gauge from title: "${product.title}"`)
  }

  // 5. stringCount — count pipe-separated values
  let stringCount = 0;
  if (individualGauges) {
    stringCount = individualGauges
      .split("|")
      .map((s) => s.trim())
      .filter((s) => s.length > 0).length;
  }
  // 6. Specifications. Metafield or nothing — never a per-line default.
  const gaugeValues = individualGauges
    .split("|")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  return {
    productLine,
    productLineSlug,
    gaugeName,
    individualGauges,
    instrument,
    stringCount,
    category,
    gaugeConflict,
    material:
      getMetafieldValue(product, "material") ??
      LINE_MATERIAL[productLineSlug] ??
      null,
    coating: getMetafieldValue(product, "coating"),
    tension: getMetafieldValue(product, "tension_profile"),
    recommendedTuning: getMetafieldValue(product, "recommended_tuning"),
    construction: null,
    auroraSetName: presetForGauges(category, gaugeValues)?.name ?? null,
  };
}

/** Short factual blurb for a product line, or null when we have none. */
export function lineBlurb(productLineSlug: string): string | null {
  return LINE_BLURB[productLineSlug] ?? null;
}

// ─── groupByProductLine ───────────────────────────────────

export function groupByProductLine(
  products: ShopifyProduct[]
): ProductFamily[] {
  // Deduplicate by handle
  const seen = new Set<string>();
  const unique = products.filter((p) => {
    if (seen.has(p.handle)) return false;
    seen.add(p.handle);
    return true;
  });

  // Filter out junk handles
  const filtered = unique.filter((p) => {
    const h = p.handle.toLowerCase();
    return !h.includes("copy-of") && !h.includes("rockpack");
  });

  // Parse each product
  type ProductWithMeta = ShopifyProduct & { meta: StringProductMeta };
  const parsed: ProductWithMeta[] = filtered
    .map((p) => {
      const meta = parseStringProduct(p);
      return { ...p, meta };
    })
    .filter((p) => ALLOWED_LINE_SLUGS.has(p.meta.productLineSlug));

  // Group into families
  const familyMap = new Map<string, ProductFamily>();
  for (const product of parsed) {
    const { productLineSlug, productLine: productLineName, instrument } =
      product.meta;
    const key = `${productLineSlug}|${instrument}`;
    if (!familyMap.has(key)) {
      familyMap.set(key, {
        productLineSlug,
        productLineName,
        instrument,
        products: [],
      });
    }
    familyMap.get(key)!.products.push(product);
  }

  // Sort products within each family by gauge order
  for (const family of familyMap.values()) {
    family.products.sort((a, b) => {
      const rankA = gaugeRank(a.meta.gaugeName);
      const rankB = gaugeRank(b.meta.gaugeName);
      if (rankA !== rankB) return rankA - rankB;
      return a.meta.gaugeName.localeCompare(b.meta.gaugeName);
    });
  }

  // Sort families by line order, then instrument alphabetically within line
  const families = Array.from(familyMap.values());
  families.sort((a, b) => {
    const lineA = lineRank(a.productLineSlug);
    const lineB = lineRank(b.productLineSlug);
    if (lineA !== lineB) return lineA - lineB;
    return a.instrument.localeCompare(b.instrument);
  });

  return families;
}
